import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import {
  CampaignSettingsCard,
  CampaignQuestionsCard,
  ConsultantCard,
  VoterDataImportCard,
  VoiceTranscriptionSettingsCard,
} from '@/components/campaign';
import { Coachmark } from '@/components/onboarding';
import { SettingsSubPageHeader } from '@/components/settings';
import { useMayManageCampaign } from '@/hooks';
import { useCoachmarkStore } from '@/stores';

export const Route = createFileRoute('/app/settings/campaign-preferences')({
  component: CampaignPreferencesPage,
});

export function CampaignPreferencesPage() {
  const voterImportRef = useRef<HTMLDivElement>(null);
  const mayImport = useMayManageCampaign();
  const coachmarkTarget = useCoachmarkStore((s) => s.target);
  const clearCoachmark = useCoachmarkStore((s) => s.clear);
  const [showVoterImportCoachmark, setShowVoterImportCoachmark] = useState(false);

  // One-shot: if onboarding (or the turf walkthrough) sent us here to import voters,
  // spotlight the card and consume the flag.
  useEffect(() => {
    if (coachmarkTarget === 'voter-import') {
      setShowVoterImportCoachmark(true);
      clearCoachmark();
    }
  }, [coachmarkTarget, clearCoachmark]);

  return (
    <div className="max-w-3xl p-6">
      <SettingsSubPageHeader
        title="Campaign preferences"
        description="Timezone, voice transcription, canvass questions, and voter data."
      />

      <div className="space-y-4 mt-6">
        <CampaignSettingsCard />
        <ConsultantCard />
        <VoiceTranscriptionSettingsCard />
        <CampaignQuestionsCard />
        {mayImport && (
          <div ref={voterImportRef} className="scroll-mt-24">
            <VoterDataImportCard />
          </div>
        )}
      </div>

      <Coachmark
        active={mayImport && showVoterImportCoachmark}
        targetRef={voterImportRef}
        message="Import your voter file here — upload your CSV and we'll dedupe and match it to your district."
        onDismiss={() => setShowVoterImportCoachmark(false)}
      />
    </div>
  );
}
